import { LeftSidebarPanelName } from '@configurator/left-sidebar/left-sidebar.model';
import { ToolBarItemName } from '@configurator/toolbars/default-toolbar/default.toolbar.component';
import { ProjectMenuItemName } from '@project/service/menu/menu.service';
import { ProjectType } from '@project/model/project-type';
import { RightSidebarPanelName } from '../right-sidebar/right-sidebar.component';
import { InterfaceMenuItemName } from '@project/service/menu/interface-menu.creator';
import { MountingMenuItemName } from '@project/service/menu/mounting-menu.creator';

interface ConfiguratorViewInfo {
  leftSidebar: LeftSidebar;
  rightSidebar: RightSidebar;
  menu: Menu;
  toolbar: ToolBarVisibility;
}

export type LeftSidebarPanelsVisibility = Record<LeftSidebarPanelName, boolean>;

export type LeftSidebar = {
  visible: true;
  panelsVisibility: LeftSidebarPanelsVisibility;
  defaultPanel: LeftSidebarPanelName;
} | {
  visible: false;
}

type ToolBarVisibility = Record<ToolBarItemName, boolean>;

type RightSidebarPanelsVisibility = Record<RightSidebarPanelName, boolean>;

type RightSidebar = {
  panelsVisibility: RightSidebarPanelsVisibility;
  /**
   * Indicates whether the mounting rail settings "gear" shall be visible in the article list.
   */
  baseArticleSettingsVisible: boolean;
  /**
   * Indicates whether the toggle article visibility "eye" is visible in the article list.
   */
  toggleArticleVisibilityVisible: boolean;
}

interface Menu {
  projectMenuItemsVisibility: ProjectMenuItemsVisibility;
  interfaceMenuItemsVisibility: InterfaceMenuItemsVisibility;
  mountingMenuItemsVisibility: MountingMenuItemsVisibility;
}

type ProjectMenuItemsVisibility = Record<ProjectMenuItemName, boolean>;

type InterfaceMenuItemsVisibility = Record<InterfaceMenuItemName, boolean>;

type MountingMenuItemsVisibility = Record<MountingMenuItemName, boolean>;

type ConfiguratorViewInfos = Record<ProjectType, ConfiguratorViewInfo>;

const defaultVisibleLeftSidebar: LeftSidebar = {
  visible: true,
  panelsVisibility: {
    searchResult: true,
    recentlyUsed: true,
    libraries: true,
    customArticles: true,
    selectGdsArticle: false,
    assistants: true,
  },
  defaultPanel: 'recentlyUsed',
}

const defaultVisibleRightSidebar: RightSidebar = {
  panelsVisibility: {
    wireList: true,
  },
  baseArticleSettingsVisible: true,
  toggleArticleVisibilityVisible: true,
}

const defaultMenu: Menu = {
  projectMenuItemsVisibility: {
    examineProject: true,
  },
  interfaceMenuItemsVisibility: {
    importExportCae: true,
    exportCad: true,
    importCsv: true,
    exportCsv: true,
    comparePartsList: true,
    importExportWsp: true,
    importProServe: true,
    printLabeling: true,
    exportDataCenter: true,
  },
  mountingMenuItemsVisibility: {
    mountingAssistant: true,
  }
}

const defaultToolBar: ToolBarVisibility = {
  displayMenu: true,
  visibilityMenu: true,
  copyPaste: true,
  accessories: true,
  comments: true,
  remove: true,
}

const configuratorViewInfos: ConfiguratorViewInfos = {
  FREE_MOUNTING: {
    toolbar: defaultToolBar,
    leftSidebar: defaultVisibleLeftSidebar,
    rightSidebar: defaultVisibleRightSidebar,
    menu: defaultMenu,
  },
  MOUNTING_RAIL: {
    toolbar: defaultToolBar,
    leftSidebar: defaultVisibleLeftSidebar,
    rightSidebar: defaultVisibleRightSidebar,
    menu: defaultMenu,
  },
  SPEEDWAY: {
    toolbar: defaultToolBar,
    leftSidebar: defaultVisibleLeftSidebar,
    rightSidebar: defaultVisibleRightSidebar,
    menu: defaultMenu,
  },
  MCS: {
    toolbar: defaultToolBar,
    leftSidebar: {
      visible: false,
    },
    rightSidebar: {
      panelsVisibility: {
        wireList: false,
      },
      baseArticleSettingsVisible: true,
      toggleArticleVisibilityVisible: false,
    },
    menu: {
      projectMenuItemsVisibility: defaultMenu.projectMenuItemsVisibility,
      interfaceMenuItemsVisibility: {
        ...defaultMenu.interfaceMenuItemsVisibility,
        importExportCae: false,
      },
      mountingMenuItemsVisibility: defaultMenu.mountingMenuItemsVisibility,
    },
  },
  GDS: {
    toolbar: defaultToolBar,
    leftSidebar: {
      visible: true,
      panelsVisibility: {
        searchResult: false,
        recentlyUsed: false,
        libraries: false,
        customArticles: false,
        selectGdsArticle: true,
        assistants: false,
      },
      defaultPanel: 'selectGdsArticle',
    },
    rightSidebar: {
      panelsVisibility: {
        wireList: false,
      },
      baseArticleSettingsVisible: true,
      toggleArticleVisibilityVisible: false,
    },
    menu: {
      projectMenuItemsVisibility: defaultMenu.projectMenuItemsVisibility,
      interfaceMenuItemsVisibility: {
        ...defaultMenu.interfaceMenuItemsVisibility,
        importExportCae: false,
      },
      mountingMenuItemsVisibility: defaultMenu.mountingMenuItemsVisibility,
    },
  },
  MARKINGSYSTEM: {
    toolbar: {
      displayMenu: false,
      visibilityMenu: false,
      copyPaste: false,
      accessories: false,
      comments: false,
      remove: false,
    },
    leftSidebar: {
      visible: false,
    },
    rightSidebar: {
      panelsVisibility: {
        wireList: false,
      },
      baseArticleSettingsVisible: false,
      toggleArticleVisibilityVisible: true,
    },
    menu: {
      projectMenuItemsVisibility: {
        examineProject: false,
      },
      interfaceMenuItemsVisibility: {
        importExportCae: false,
        exportCad: false,
        importCsv: false,
        exportCsv: false,
        comparePartsList: true,
        importExportWsp: true,
        importProServe: false,
        printLabeling: false,
        exportDataCenter: false,
      },
      mountingMenuItemsVisibility: {
        mountingAssistant: false,
      },
    },
  },
  CIRCUIT_BOARD: {
    toolbar: defaultToolBar,
    leftSidebar: defaultVisibleLeftSidebar,
    rightSidebar: defaultVisibleRightSidebar,
    menu: defaultMenu,
  },
  XCOM: {
    toolbar: defaultToolBar,
    leftSidebar: defaultVisibleLeftSidebar,
    rightSidebar: defaultVisibleRightSidebar,
    menu: {
      projectMenuItemsVisibility: defaultMenu.projectMenuItemsVisibility,
      interfaceMenuItemsVisibility: {
        ...defaultMenu.interfaceMenuItemsVisibility,
        exportCad: false,
        importCsv: true,
        exportCsv: true,
        comparePartsList: true,
        importExportWsp: true,
        importProServe: true,
        printLabeling: true,
        exportDataCenter: true,
      },
      mountingMenuItemsVisibility: defaultMenu.mountingMenuItemsVisibility,
    },
  },
  ADMIN: {
    toolbar: defaultToolBar,
    leftSidebar: defaultVisibleLeftSidebar,
    rightSidebar: defaultVisibleRightSidebar,
    menu: defaultMenu,
  },
  SPLICING_CONNECTOR_221: {
    toolbar: defaultToolBar,
    leftSidebar: {
      visible: false,
    },
    rightSidebar: {
      panelsVisibility: {
        wireList: false,
      },
      baseArticleSettingsVisible: false,
      toggleArticleVisibilityVisible: false,
    },
    menu: {
      projectMenuItemsVisibility: defaultMenu.projectMenuItemsVisibility,
      interfaceMenuItemsVisibility: {
        importExportCae: false,
        exportCad: false,
        importCsv: false,
        exportCsv: false,
        comparePartsList: false,
        importProServe: false,
        printLabeling: false,
        exportDataCenter: false,
        importExportWsp: true,
      },
      mountingMenuItemsVisibility: defaultMenu.mountingMenuItemsVisibility,
    },
  },
  LTL: {
    toolbar: {
      displayMenu: true,
      visibilityMenu: false,
      copyPaste: false,
      accessories: false,
      comments: true,
      remove: false,
    },
    leftSidebar: {
      visible: false,
    },
    rightSidebar: {
      panelsVisibility: {
        wireList: false,
      },
      baseArticleSettingsVisible: false,
      toggleArticleVisibilityVisible: false,
    },
    menu: defaultMenu,
  }
}

export function getConfiguratorViewInfo(projectType: ProjectType | undefined): ConfiguratorViewInfo {
  if (!projectType || !configuratorViewInfos[projectType]) {
    return {
      toolbar: defaultToolBar,
      leftSidebar: {visible: false},
      rightSidebar: {
        panelsVisibility: {
          wireList: false,
        },
        baseArticleSettingsVisible: false,
        toggleArticleVisibilityVisible: false,
      },
      menu: {
        projectMenuItemsVisibility: defaultMenu.projectMenuItemsVisibility,
        interfaceMenuItemsVisibility: {
          importExportCae: false,
          exportCad: false,
          importCsv: false,
          exportCsv: false,
          comparePartsList: false,
          importExportWsp: false,
          importProServe: false,
          printLabeling: false,
          exportDataCenter: false,
        },
        mountingMenuItemsVisibility: {
          mountingAssistant: false,
        }
      },
    };
  }

  return structuredClone(configuratorViewInfos[projectType]);
}

export function getConfiguratorViewInfoForWago(projectType: ProjectType | undefined): ConfiguratorViewInfo {
  const info = getConfiguratorViewInfo(projectType);
  if (projectType === 'MARKINGSYSTEM') {
    return info;
  }

  const leftSidebarPanelsVisibility = info.leftSidebar.visible ? info.leftSidebar.panelsVisibility : {
    searchResult: false,
    recentlyUsed: false,
    libraries: false,
    customArticles: false,
    selectGdsArticle: false,
    assistants: false,
  };
  leftSidebarPanelsVisibility.searchResult = true;
  leftSidebarPanelsVisibility.recentlyUsed = true;

  const previous = info.leftSidebar;
  info.leftSidebar = {
    visible: true,
    panelsVisibility: leftSidebarPanelsVisibility,
    defaultPanel: previous.visible
      ? previous.defaultPanel
      : 'recentlyUsed',
  };
  if (projectType === 'LTL') {
    info.toolbar.accessories = true;
    info.toolbar.copyPaste = true;
    info.toolbar.remove = true;
  }

  return info
}

export function getConfiguratorViewInfoForProduction(projectType: ProjectType | undefined): ConfiguratorViewInfo {
  const info = getConfiguratorViewInfoForWago(projectType);

  if(info.leftSidebar.visible) {
    info.leftSidebar.panelsVisibility.libraries = false;
    info.leftSidebar.panelsVisibility.customArticles = false;
    info.leftSidebar.panelsVisibility.selectGdsArticle = false;
  }

  return info;
}
